import { useState, useEffect } from "react";
import { Activity, Filter, Calendar, FileText, Receipt, Car, Users, Search, Download, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminActivity({ user }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: "",
    user_id: "",
    date_from: "",
    date_to: ""
  });
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activityRes, usersRes] = await Promise.all([
        authFetch(`${API}/admin/activity-log`, { }),
        authFetch(`${API}/admin/users`, { })
      ]);
      
      if (activityRes.ok) setActivities(await activityRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await authFetch(`${API}/admin/activity-log?${params}`, {
      });

      if (response.ok) {
        setActivities(await response.json());
      }
    } catch (error) {
      toast.error("Errore nella ricerca");
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = () => {
    if (activities.length === 0) {
      toast.error("Nessun log da esportare");
      return;
    }

    const csvContent = [
      ['Data', 'Tipo', 'Utente', 'Descrizione', 'IP'].join(','),
      ...activities.map(a => [
        a.timestamp,
        a.type,
        a.user_name,
        `"${a.description}"`,
        a.ip_address || '-'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity_log_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success("Export completato");
  };

  const clearFilters = () => {
    setFilters({
      type: "",
      user_id: "",
      date_from: "",
      date_to: ""
    });
  };

  const typeIcons = {
    quote: FileText,
    invoice: Receipt,
    acceptance: Car,
    appointment: Calendar,
    user: Users,
    login: Users,
    logout: Users,
    default: Activity
  };

  const typeLabels = {
    quote: "Preventivo",
    invoice: "Fattura",
    acceptance: "Accettazione",
    appointment: "Appuntamento",
    user: "Utente",
    login: "Login",
    logout: "Logout"
  };

  const typeColors = {
    quote: "text-orange-400",
    invoice: "text-green-400",
    acceptance: "text-purple-400",
    appointment: "text-blue-400",
    user: "text-yellow-400",
    login: "text-emerald-400",
    logout: "text-red-400"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-activity-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Log Attività</h1>
          <p className="text-zinc-500 mt-1">{activities.length} eventi registrati</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-zinc-700"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtri
          </Button>
          <Button onClick={exportLogs} className="btn-primary">
            <Download className="w-4 h-4 mr-2" />
            Esporta
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold uppercase font-['Barlow_Condensed']">Filtri</h3>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400">
              <X className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="form-group">
              <label className="form-label">Tipo Attività</label>
              <select
                className="form-select"
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
              >
                <option value="">Tutti</option>
                <option value="quote">Preventivi</option>
                <option value="invoice">Fatture</option>
                <option value="acceptance">Accettazioni</option>
                <option value="appointment">Appuntamenti</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Utente</label>
              <select
                className="form-select"
                value={filters.user_id}
                onChange={(e) => setFilters({...filters, user_id: e.target.value})}
              >
                <option value="">Tutti</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Data Da</label>
              <input
                type="date"
                className="form-input"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Data A</label>
              <input
                type="date"
                className="form-input"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleSearch} className="btn-primary">
              <Search className="w-4 h-4 mr-2" />
              Cerca
            </Button>
          </div>
        </div>
      )}

      {/* Activity List */}
      {activities.length > 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-950">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-500 uppercase">Data/Ora</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-500 uppercase">Utente</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-500 uppercase">Descrizione</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {activities.map((activity, index) => {
                  const Icon = typeIcons[activity.type] || typeIcons.default;
                  return (
                    <tr key={index} className="hover:bg-zinc-800/30">
                      <td className="px-4 py-3 text-sm font-['JetBrains_Mono'] text-zinc-400">
                        {activity.timestamp}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${typeColors[activity.type] || 'text-zinc-400'}`} />
                          <span className="text-sm text-white">
                            {typeLabels[activity.type] || activity.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {activity.user_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-300">
                        {activity.description}
                      </td>
                      <td className="px-4 py-3 text-sm font-['JetBrains_Mono'] text-zinc-500">
                        {activity.ip_address || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Activity className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessuna attività</p>
          <p className="empty-state-text">Non ci sono log di attività da visualizzare</p>
        </div>
      )}
    </div>
  );
}
